import { useCallback, useEffect, useRef, useState } from 'react';
import { message, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSpin } from '@/components/SpinContext.jsx';
import { useToken } from '@/common/helper';
import { getPermissions } from '@/common/helper/getPermissions';
import StatusTag from '@/components/StatusTag.jsx';
import {
  DeleteCustomerCredential,
  GetCustomerCredentialsPage,
  RotateCustomerCredentialSecret,
  UpdateCustomerCredential,
} from '@/apis/customerCredentials';
import type {
  CredentialData,
  UpdateCredentialPayload,
} from '@/apis/customerCredentials';
import CredentialRowMenu from '../components/CredentialRowMenu';
import type { CredentialRowAction } from '../components/CredentialRowMenu';
import type { SaveOutcome } from '../components/CredentialEditForm';
import { formatLastUsed } from '../credentialFormat';
import type {
  CredentialDrawerMode,
  CredentialsController,
  RootState,
} from '../types';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// The API functions reject with the axios response, so the server's message
// sits at `data.data.message`. One cast to the narrowest shape, since the
// rejection is typed `unknown`.
const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { data?: { message?: string } } })?.data?.data?.message ||
  fallback;

/** Holds the Credentials page's state and handlers: the paged, searchable
 * list, the create/edit/rotate drawers, delete, and the table columns. */
export const useCredentialsController = (): CredentialsController => {
  const { token } = useToken();
  const { t } = useTranslation('settings');
  const { showSpin, hideSpin } = useSpin();
  const mainMenu = useSelector((state: RootState) => state.menu.items);
  const credentialsPermissions = getPermissions(
    mainMenu,
    'Settings',
    'Credentials'
  );
  // The backend gates create/edit/rotate/delete on the Credentials create
  // permission (default:admin); mirror that so the UI offers only what works.
  const canManage = Boolean(credentialsPermissions?.is_create);

  const [credentialList, setCredentialList] = useState<CredentialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawerMode, setDrawerMode] = useState<CredentialDrawerMode>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedCredential, setSelectedCredential] =
    useState<CredentialData | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const requestId = useRef(0);

  const fetchCredentials = useCallback(
    (silent = false): void => {
      const thisRequest = ++requestId.current;
      if (!silent) setLoading(true);
      GetCustomerCredentialsPage(token, {
        search,
        page,
        pageSize: PAGE_SIZE,
      })
        .then((res) => {
          // A newer request (another page or search) supersedes this one.
          if (thisRequest !== requestId.current) return;
          const data = res?.data?.data;
          if (data?.is_success) {
            setCredentialList(data.data || []);
            setTotalCount(data.total_count ?? data.data?.length ?? 0);
            setTotalPages(Math.max(1, data.page_count ?? 1));
            setLoadFailed(false);
          } else {
            setLoadFailed(true);
            message.error(data?.message || 'Failed to fetch credentials.');
          }
        })
        .catch((error) => {
          if (thisRequest !== requestId.current) return;
          setLoadFailed(true);
          console.error('Error fetching credentials:', error);
        })
        .finally(() => {
          if (thisRequest === requestId.current) setLoading(false);
        });
    },
    [token, search, page]
  );

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  // Debounce typing into the search box; a new search starts on page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch((current) => {
        const next = searchInput.trim();
        if (current !== next) setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Verification runs before a save's response comes back, so re-reading the
  // list once is enough to show the settled status. (API-key credentials have
  // nothing to verify against and stay "Checking Credentials" until their
  // first run reports its health, so there is nothing to poll for.)
  const refreshAfterChange = useCallback((): void => {
    fetchCredentials(true);
  }, [fetchCredentials]);

  const closeDrawer = useCallback((): void => {
    setDrawerMode(null);
    setSelectedCredential(null);
  }, []);

  const handleAddCredential = (): void => {
    setSelectedCredential(null);
    setDrawerMode('create');
  };

  // The create drawer saves and toasts itself; the page only closes it and
  // re-reads the list.
  const handleCredentialCreated = (): void => {
    closeDrawer();
    refreshAfterChange();
  };

  // Tells the user how a save ended. Verification runs before the response, so
  // a rejected OAuth secret comes back as a Failed credential, not an error.
  const reportSaved = (
    saved: CredentialData | undefined,
    done: string
  ): void => {
    if (saved?.status === 'ERROR') {
      message.warning(
        `${done} Verification failed${
          saved.status_detail ? `: ${saved.status_detail}` : '.'
        }`
      );
    } else {
      message.success(done);
    }
  };

  const handleEditSave = async (
    payload: UpdateCredentialPayload
  ): Promise<SaveOutcome> => {
    if (!selectedCredential?.id) return { ok: false };
    setSaving(true);
    showSpin();
    try {
      const res = await UpdateCustomerCredential(
        token,
        selectedCredential.id,
        payload
      );
      const data = res?.data?.data;
      if (data?.is_success) {
        reportSaved(data.data, data.message || 'Credential updated.');
        closeDrawer();
        refreshAfterChange();
        return { ok: true };
      }
      const failure = data?.message || 'Failed to update credential.';
      // A taken name is shown under the Name field by the form itself.
      if (failure !== 'A credential with this name already exists') {
        message.error(failure);
      }
      return { ok: false, message: failure };
    } catch (error) {
      console.error('Error updating credential:', error);
      const failure = errorMessage(error, 'Failed to update credential.');
      if (failure !== 'A credential with this name already exists') {
        message.error(failure);
      }
      return { ok: false, message: failure };
    } finally {
      setSaving(false);
      hideSpin();
    }
  };

  const handleRotateSecret = (secret: string): void => {
    if (!selectedCredential?.id) return;

    setSaving(true);
    showSpin();
    RotateCustomerCredentialSecret(token, selectedCredential.id, secret)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          reportSaved(data.data, data.message || 'Secret rotated.');
          closeDrawer();
          refreshAfterChange();
        } else {
          message.error(data?.message || 'Failed to rotate secret.');
        }
      })
      .catch((error) => {
        console.error('Error rotating credential secret:', error);
        message.error(errorMessage(error, 'Failed to rotate secret.'));
      })
      .finally(() => {
        setSaving(false);
        hideSpin();
      });
  };

  const openDeleteDialog = (credential: CredentialData): void => {
    setSelectedCredential(credential);
    setDeleteModalVisible(true);
  };

  const handleRowAction = (
    action: CredentialRowAction,
    credential: CredentialData
  ): void => {
    if (action === 'delete') {
      openDeleteDialog(credential);
      return;
    }
    setSelectedCredential(credential);
    setDrawerMode(action);
  };

  const handleConfirmDelete = (): void => {
    if (!selectedCredential?.id) return;

    DeleteCustomerCredential(token, selectedCredential.id)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data?.message || 'Credential deleted successfully!');
          setDeleteModalVisible(false);
          setSelectedCredential(null);
          // Deleting the last row of a later page steps back a page.
          if (credentialList.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            refreshAfterChange();
          }
        } else {
          message.error(data?.message || 'Failed to delete credential.');
        }
      })
      .catch((error) => {
        // The interceptor has already toasted the failure.
        console.error('Error deleting credential:', error);
      });
  };

  const columns: ColumnsType<CredentialData> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      width: '24%',
      render: (text: string) => (
        <Typography.Text ellipsis={{ tooltip: text }} strong>
          {text || '--'}
        </Typography.Text>
      ),
    },
    {
      title: t('details'),
      dataIndex: 'details',
      key: 'details',
      width: '18%',
      render: (text: string) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '--'}
        </Typography.Text>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status: string, record: CredentialData) => (
        <StatusTag
          status={status}
          title={status === 'ERROR' ? record.status_detail : undefined}
        />
      ),
    },
    {
      title: t('lastUsed'),
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: '16%',
      render: (value: string | null) => formatLastUsed(value),
    },
    {
      title: t('createdBy'),
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: '22%',
      render: (text: string | null) => text || '--',
    },
    {
      title: '',
      key: 'action',
      width: 56,
      render: (_: unknown, record: CredentialData) =>
        canManage ? (
          <CredentialRowMenu credential={record} onAction={handleRowAction} />
        ) : null,
    },
  ];

  const rangeStart =
    credentialList.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd =
    credentialList.length === 0 ? 0 : rangeStart + credentialList.length - 1;

  let emptyText = 'No credentials yet.';
  if (loadFailed) emptyText = "Couldn't load credentials. Please try again.";
  else if (search) emptyText = 'No credentials match your search.';

  return {
    columns,
    credentialList,
    credentialsPermissions,
    deleteModalVisible,
    drawerMode,
    emptyText,
    handleAddCredential,
    handleCloseDrawer: closeDrawer,
    handleConfirmDelete,
    handleCredentialCreated,
    handleEditSave,
    handlePageChange: setPage,
    handleRotateSecret,
    handleSearch: setSearchInput,
    loading,
    page,
    rangeEnd,
    rangeStart,
    saving,
    selectedCredential,
    setDeleteModalVisible,
    totalCount,
    totalPages,
    t,
  };
};
